import { AfterContentInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, } from '@angular/core';
import get from 'lodash/get';
import map from 'lodash/map';
import find from 'lodash/find';
import keyBy from 'lodash/keyBy';
import every from 'lodash/every';
import maxBy from 'lodash/maxBy';

import extend from 'lodash/extend';
import filter from 'lodash/filter';
import forOwn from 'lodash/forOwn';
import reduce from 'lodash/reduce';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import isObject from 'lodash/isObject';
import includes from 'lodash/includes';

import cloneDeep from 'lodash/cloneDeep';
import mergeWith from 'lodash/mergeWith';
import isNil from 'lodash/isNil';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ScrollToConfigOptions, ScrollToService } from '@nicky-lenaers/ngx-scroll-to';

import {
  CustomComponent,
  CUSTOM_NOTE_CONTEXT,
  dataHelper,
  errorHandler,
  IBakeryCountry,
  IBakeryScheduleDay,
  IDeliveryCheckInfo,
  IFormattedBakerySchedule,
  IFormattedDeviatingDeadline,
  IProductType,
  IQuestionHelpTextDetails,
  ISchedule,
  IUpsellProductType,
  getEffectiveMaxQuantity,
  getDailyLimitQuantity,
  getStockQuantity,
  ngHelper,
  PRODUCT_TYPE_STRUCTURE_TYPES,
  scheduleHelper,
  UiContextService
} from '@cakeiteasy/web-utils';

import {
  ADDONS_TYPES,
  CART_DELIVERY_TYPES,
  extendWithPrice,
  getActualLinesFromEvent,
  getCakeCartLineTotalPrice,
  getEmptyCartLine,
  getPiecesMultipleVariantsPriceAndQuantity,
  getUpsellCartLinesByProductTypeId,
  getUpsellGroupedPriceAndQuantity,
  ICakeCartLine,
  ICartLine,
  IUpsellCartLinesByProductTypeId,
  IUpsellCartLinesGroup,
  makeCartLinesFromAddons,
  makeSizeProductForOrderLine
} from '../cart';

import {
  canHasPrinting,
  extendWithMinPrice,
  extendWithPriceDiff,
  applySelectionLimit,
  getDefaultSelection,
  getExtraProductGroup,
  getPreselectedSelection,
  filterAddOnsByApplyOnceValue,
} from './cake-builder.helper';
import { ISubmitBuilderPayload } from './interfaces';

const HELP_TEXT_KEYS = ['sizeSectionHelpText', 'filingSectionHelpText'];

@Component({
  standalone: false,
  selector: 'cake-builder',
  templateUrl: './cake-builder.component.html',
  styleUrls: ['./cake-builder.component.scss'],

})
export class CakeBuilderComponent extends CustomComponent implements OnChanges, AfterContentInit {
  @ViewChild('cakeVariantsSection', { static: false }) variantsSection: ElementRef<HTMLElement>;
  @ViewChild('optionalSections', { static: false }) optionalSections: ElementRef<HTMLElement>;

  @Input() cake: IProductType;
  @Input() upsellProductTypes: IUpsellProductType[];

  @Input() bakeryUrl: string;
  @Input() showBakeryInfo = true;
  @Input() deliveryDateChecker: (productTypeId: number, date: Date) => Promise<IDeliveryCheckInfo>;
  @Input() cartLine: ICakeCartLine;
  @Input() cartLines: ICakeCartLine[];
  @Input() upsellCartLines: ICakeCartLine[];
  @Input() disabledChooseMotiveModal = false;
  @Input() disabledScrollingToSection = true;
  @Input() disabledBackButton = false;
  @Input() showCakeInfo = true;
  @Input() showSingleButtonForExistingOrderLine = true;
  @Input() showOnlySaveButton = false;
  @Input() addMoreButtonText: string;
  @Input() saveButtonText: string;
  @Input() cakeSaving: Promise<any>;
  @Input() standAloneMode = false;
  @Input() showBakeryDeadline = true;
  @Input() showCustomNote = false;
  @Input() noteContext: CUSTOM_NOTE_CONTEXT = CUSTOM_NOTE_CONTEXT.WEBSHOP;
  @Input() bakeryDetails: IQuestionHelpTextDetails;
  @Input() showSubmitButtonsAsSticky = true;
  @Input() moveStickyBottomButtons = false;

  @Output() onBakeryNameClicked = new EventEmitter<string>();
  @Output() onBack = new EventEmitter<void>();
  @Output() onLineUpdated = new EventEmitter<ICartLine>();
  @Output() onSaveCartLine = new EventEmitter<ICakeCartLine>();
  @Output() onSubmit = new EventEmitter<ISubmitBuilderPayload>();
  @Output() onCheckDelivery = new EventEmitter<{
    deliveryType: CART_DELIVERY_TYPES,
    bakeryId: number,
    country: IBakeryCountry,
    productTypeId: number
  }>();

  public STRUCTURE_TYPES = PRODUCT_TYPE_STRUCTURE_TYPES;
  public isConcreteProductType = true;
  public isCateringMenu = false;

  public loaded = false;
  public bakeryCurrency: { total: string; symbol: string };
  public orderDeadlines: IFormattedBakerySchedule[] | string;
  public specialDeadlines: IFormattedDeviatingDeadline[];

  public sizes: any[];
  public variants: any[];
  public printingPictures: any[];
  public extraProductGroups: any[];
  public questions: any[];
  public sortingAddons: any[] = [];
  public deposits: any[];
  public details: IQuestionHelpTextDetails;
  public printingPictureMap: { [key: number]: any } = {};

  public ADDONS_TYPES = ADDONS_TYPES;

  public totalPrice = 0;
  public totalQuantity = 0;
  public totalPriceUpsell = 0;
  public totalQuantityUpsell = 0;
  public piecesVariantCartLines: { [key: number]: ICakeCartLine } = {};
  public countPerVariantMap: { [key: number]: number } = {};
  public isPiecesVariantsCanBeSaved: boolean;

  public stockLeft: number | null;
  public dailyLimit: number | null;
  public effectiveMaxQuantity: number | null;

  public upsellAddonsByProductTypeId: IUpsellCartLinesByProductTypeId = {};
  public canUpsellsBeSaved = true;

  public isRequirementsFilled = true;
  public isRequirementsExist = false;
  public requiredSections = {
    printingPictures: []
  };
  public requirementsFilling: { printingPictures?: { [key: number]: boolean } } = { printingPictures: {} };
  public _cakeSaving: Promise<any>;
  public DELIVERY_TYPES = CART_DELIVERY_TYPES;

  private _subjectsSet: { [key: string]: Subject<any> } = {
    'cartLine': new BehaviorSubject<ICakeCartLine>(null),
    'cake': new BehaviorSubject<IProductType>(null)
  };

  public get isPiecesWithMultipleVariants() {
    return this.cake && this.cake.pieces && !isEmpty(this.variants) && this.variants.length > 1;
  }

  public get isPiecesGroupType() {
    return this.cake && this.cake.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.PIECES_GROUP;
  }

  public get isConcreteProductTypeNotEmpty() {
    const piecesType = this.isPiecesGroupType || this.isSinglePiecesCake;
    const shouldShowTitle = this.showTitleForComplex || this.isSinglePiecesWithAddons || piecesType;
    const isValidMode = this.standAloneMode && !this.isSinglePiecesNoAddons && !this.isComplexOneSizeNoAddonsNoVariants;

    const cakeTitleRenders = shouldShowTitle && isValidMode;

    const cakeSizeSectionRenders = !this.cake?.pieces && this.cake?.sizes?.length > 1;
    const cakeVariantsSectionRenders = (this.variants && this.variants.length > 1 && !this.isPiecesWithMultipleVariants)
      || this.isPiecesGroupType;
    const optionalSectionRenders = (this.sortingAddons.length > 0) ||
      (this.cake?.greetingCard?.available && this.cartLine?.greetingCard && !this.isPiecesGroupType);

    return (
      cakeTitleRenders ||
      cakeSizeSectionRenders ||
      cakeVariantsSectionRenders ||
      optionalSectionRenders
    );
  }

  public get isSinglePiecesCake() {
    return this.cake && this.cake.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.PIECES;
  }

  public get isSinglePiecesWithAddons() {
    return this.isSinglePiecesCake && !isEmpty(this.extraProductGroups);
  }

  public get isSinglePiecesNoAddons() {
    return this.isSinglePiecesCake && this._addOnsIsEmpty();
  }

  public get isComplexOneSizeNoAddonsNoVariants() {
    return this.cake && this.cake.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.COMPLEX
      && this.cake.sizes.length === 1 && this.variants.length === 1 && this._addOnsIsEmpty();
  }

  public get hasOnlyGreetingCard() {
    return this.cake && this.cake.greetingCard.available
      && isEmpty(this.extraProductGroups) && isEmpty(this.questions) && isEmpty(this.printingPictures);
  }

  public get showTitleForComplex() {
    return this.cake && this.cake.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.COMPLEX
      && (this.cake.sizes.length > 1 || this.variants.length > 1 || !this.hasOnlyGreetingCard);
  }

  public get isMultiVariantSubmitDisabled() {
    return !this.isPiecesVariantsCanBeSaved || (this.isRequirementsExist && !this.isRequirementsFilled);
  }

  public get isBuilderSectionsDisabled() {
    return this.cartLine && !this.cartLine.variant && !this.isPiecesWithMultipleVariants
      || this.isRequirementsExist && !this.isRequirementsFilled;
  }

  public get isUpsellsSubmitButtonsDisabled() {
    return this.isConcreteProductType && (this.isPiecesGroupType && !this.isPiecesVariantsCanBeSaved || this.isBuilderSectionsDisabled)
      || !(this.totalQuantity + this.totalQuantityUpsell) || !this.canUpsellsBeSaved;
  }

  constructor(public uiService: UiContextService,
              public _scrollTo: ScrollToService,
              private _translate: TranslateService) {
    super();
  }

  public checkDelivery(deliveryType: CART_DELIVERY_TYPES) {
    const { bakery: { id: bakeryId, country }, id: productTypeId } = this.cake;
    this.onCheckDelivery.emit({ deliveryType, country, bakeryId, productTypeId });
  }

  public canHasMotive() {
    if (!this.cake || !this.cartLine) {
      return false;
    }

    return canHasPrinting('printingPictures', this.cartLine.size, this.sizes);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'cartLine', (cartLine) => {
      this._subjectsSet['cartLine'].next(cartLine);
    });

    ngHelper.ifChanged(changes, 'cartLines', (cartLines) => this._initCartLines(cartLines));

    if (changes.upsellProductTypes || changes.upsellCartLines) {
      this._initUpsellCartLines(this.upsellCartLines);
    }

    ngHelper.ifChanged(changes, 'cake', (cake: IProductType) => {
      this.isCateringMenu = cake.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.CATERING_MENU;
      this.isConcreteProductType = !this.isCateringMenu;

      this.bakeryCurrency = {
        total: get(cake, 'bakery.country.currencySymbol'),
        symbol: get(cake, 'bakery.country.currencySymbol')
      };

      if (cake.bakery.customSchedule) {
        this.orderDeadlines = cake.bakery.customSchedule;
      } else {
        const { hasCustomDeadlines, deadlines, bakery: { deviatingOrderDeadlines } } = cake;
        const schedule: ISchedule<IBakeryScheduleDay> = hasCustomDeadlines ? deadlines : cake.bakery.schedule;
        this.orderDeadlines = scheduleHelper.getBakeryDeadlineRules(schedule, cake.daysUnavailable);
        this._setSpecialDeadlines(cake, schedule, deviatingOrderDeadlines);
      }

      this.stockLeft = getStockQuantity(this.cake);
      this.dailyLimit = getDailyLimitQuantity(this.cake);
      this.effectiveMaxQuantity = getEffectiveMaxQuantity(this.cake);

      this.details = this._getDetails();
      this._subjectsSet['cake'].next(cake);
    });

    ngHelper.ifChanged(changes, 'cakeSaving', (cakeSaving) => {
      this._cakeSaving = new Promise((resolve) => {
        return resolve(cakeSaving);
      });
    });
  }

  public ngAfterContentInit() {
    this._subscribe();
  }

  private _setSpecialDeadlines(cake: IProductType,
                               schedule: ISchedule<IBakeryScheduleDay>,
                               deviatingOrderDeadlines = []) {
    const nonWorkingDays = [
      ...(cake.bakery.holidays || []),
      ...(cake.bakery.closedDays || []),
    ];

    this.specialDeadlines = scheduleHelper.getDeviatingDeadlines(
      schedule,
      deviatingOrderDeadlines,
      nonWorkingDays
    );
  }

  public cakeCounterUpdated(cartLine: ICartLine) {
    this._updateTotalPriceAndQuantity();
    this.onLineUpdated.emit(cartLine);
  }

  public onSizeUpdated(newSize: any) {
    if (!this.cartLine.size
      && !this.cake.pieces
      && this.cake.sizes.length > 1) {
      setTimeout(() => {
        if (this.variantsSection) {
          this._scrollToSection(this.variantsSection);
        } else {
          this._scrollToSection(this.optionalSections, 450, -100);
        }
      });
    }

    this.cartLine.size = newSize;
    this._onSizeUpdated(newSize);
    this._updateTotalPriceAndQuantity();
    this.onLineUpdated.emit(this.cartLine);
  }

  public onFillingUpdated(filling) {
    if (!this.cartLine.variant) {
      this._scrollToSection(this.optionalSections, 450, -100);
    }

    this.cartLine.variant = filling;
    this._updateProductInfo();
    this._updateTotalPriceAndQuantity();
    this.onLineUpdated.emit(this.cartLine);
  }

  public onMultipleFillingUpdated(event: { variant: any; count: number }) {
    const { variant, count } = event;

    const product = find(this.cartLine.size.products, (product) => {
      return product.variant.id === variant.id;
    });

    if (this.piecesVariantCartLines[product.id]) {
      this.piecesVariantCartLines[product.id].count = count;
    } else {
      const deposits = this.cartLine.size.deposits;

      this.piecesVariantCartLines[product.id] = extend(
        getEmptyCartLine(this.cake, null),
        {
          size: this.cartLine.size,
          productId: product.id,
          count,
          variant,
          productType: this.cake,
          questionsMap: this.cartLine.questionsMap,
          deposit: deposits && deposits.length ? deposits[0] : {},
        }
      );
    }

    this.piecesVariantCartLines[product.id].product = makeSizeProductForOrderLine(this.cake, product);
    extendWithPrice(this.piecesVariantCartLines[product.id], this.cartLine.size, product);

    this._updateTotalPriceAndQuantity();
    this.isPiecesVariantsCanBeSaved = this._checkPiecesVariantSaving();
  }

  public onUpsellProductUpdated(event: { productTypeId: number; productId: number; count: number }) {
    const { productTypeId, productId, count } = event;

    this.upsellAddonsByProductTypeId[productTypeId].lines[productId].count = count;

    this.canUpsellsBeSaved = this._canUpsellsBeSaved();

    // TODO: Should be uncommented for processing on task NS3-6280
    // if (this.isCateringMenu) {
    //   this.onSaveCartLine.emit(this.upsellAddonsByProductTypeId[productTypeId].lines[productId]);
    // }

    this._updateTotalPriceAndQuantity();
  }

  public onUpsellAddonsUpdated(event: {
    productTypeId: number,
    addonsLine: IUpsellCartLinesGroup['addonsLine'] | null
  }) {
    if (!event || !this.upsellAddonsByProductTypeId[event.productTypeId]) {
      return;
    }

    this.upsellAddonsByProductTypeId[event.productTypeId].addonsLine = event.addonsLine;

    this.canUpsellsBeSaved = this._canUpsellsBeSaved();
    this._updateTotalPriceAndQuantity();
  }

  private _canUpsellsBeSaved(): boolean {
    return values(this.upsellAddonsByProductTypeId)
      .reduce((acc, { lines }) => {
        const upsellLines = values(lines || {});
        const count = upsellLines.reduce((result, line) => result + line.count, 0);
        const minQuantity = get(upsellLines[0], 'productType.minQuantity');

        return acc && (!minQuantity || minQuantity === 1 || !count || count >= minQuantity);
      }, true);
  }

  public onPrintingPictureSelected(addOn, printingPicture) {
    this.printingPictureMap[addOn.id] = printingPicture;
    this.cartLine.imagesForPrinting = Object.keys(this.printingPictureMap)
      .filter(key => this.printingPictureMap[key].id)
      .map(key => this.printingPictureMap[key]);

    this.onLineUpdated.emit(this.cartLine);

    if (this.requiredSections.printingPictures.length) {
      this.requiredSections.printingPictures = this.requiredSections.printingPictures
        .map(pic => {
          if (pic.id === addOn.id) {
            pic.checked = Boolean(printingPicture.url);
          }
          return pic;
        });
      this._checkRequirementsFilling();
    }

    // to save printingPicture for PiecesWithMultipleVariants
    if (this.isPiecesGroupType) {
      forOwn(this.piecesVariantCartLines, (cartLine) => {
        cartLine.imagesForPrinting = [...this.cartLine.imagesForPrinting];
      });
    }

    this._updateTotalPriceAndQuantity();
  }

  public onQuestionAnswerChange(answer: string, lineQuestion: any) {
    lineQuestion.answer = answer;

    // to save question map for PiecesWithMultipleVariants
    if (this.isPiecesGroupType) {
      forOwn(this.piecesVariantCartLines, (cartLine) => {
        cartLine.questionsMap[lineQuestion.id] = lineQuestion;
      });
    }

    this._updateTotalPriceAndQuantity();
    this.onLineUpdated.emit(this.cartLine);
  }

  public greetingCardPriceCalculation() {
    if (this.cartLine.greetingCard.checked && this.cartLine.greetingCard.text) {
      this.cartLine.greetingCard.price = this.cake.greetingCard.price;
    } else {
      this.cartLine.greetingCard.price = 0;
    }

    this._updateTotalPriceAndQuantity();
    this.onLineUpdated.emit(this.cartLine);
  }

  public onExtraProductSelected(extraProducts: any[], groupId: number) {
    this.cartLine.extraProductsMap[groupId] = extraProducts;
    this.onLineUpdated.emit(this.cartLine);

    // to save extra product map for PiecesWithMultipleVariants
    if (this.isPiecesGroupType) {
      forOwn(this.piecesVariantCartLines, (cartLine) => {
        cartLine.extraProductsMap[groupId] = extraProducts;
      });
    }

    this._updateTotalPriceAndQuantity();
  }

  public submit(event: { goNext: boolean, addMore: boolean }) {
    const { goNext, addMore } = event;
    const cartLine = filterAddOnsByApplyOnceValue(this.cartLine);
    const upsellLines = this.getValidUpsellLinesWithAddons();
    const applyOnceLines = this.prepareApplyOnceLines();

    let payload: { [key: string]: ICakeCartLine[] | ICakeCartLine | boolean } = { upsellLines, applyOnceLines, goNext, addMore };

    if (this.isCateringMenu) {
      payload = { ...payload, lines: upsellLines, upsellLines: [] };
    } else if (this.isPiecesGroupType) {
      const { extraProductsMap, questionsMap, imagesForPrinting, deposit } = cartLine;
      const lines: ICakeCartLine[] = values(this.piecesVariantCartLines)
        .map((l: ICakeCartLine) => ({ ...l, extraProductsMap, questionsMap, imagesForPrinting, deposit }));

      payload = { ...payload, lines };
    } else if (this.isConcreteProductType) {
      payload = { ...payload, line: cartLine };
    }

    this._trackSubmit(payload);
    this.onSubmit.emit(payload);
  }

  private prepareApplyOnceLines(): ICakeCartLine[] {
    const { bakery: { id: bakeryId } } = this.cake;
    const cartLineAddons = makeCartLinesFromAddons({
      cartLine: filterAddOnsByApplyOnceValue(this.cartLine, true),
      bakeryId
    });

    const upsellLinesAddons = values(this.upsellAddonsByProductTypeId).reduce((acc, { lines, addonsLine }) => {
      const hasSelectedLines = values(lines || {}).some(({ count }) => count);
      if (!hasSelectedLines) {
        return acc;
      }

      acc.push(
        ...makeCartLinesFromAddons({
          cartLine: filterAddOnsByApplyOnceValue(addonsLine as ICakeCartLine, true),
          bakeryId,
        })
      );
      return acc;
    }, []);

    return [...cartLineAddons, ...upsellLinesAddons];
  }

  private getValidUpsellLinesWithAddons(): ICakeCartLine[] {
    return values(this.upsellAddonsByProductTypeId).reduce((acc, { lines, addonsLine }) => {
      const filteredAddonsLine = filterAddOnsByApplyOnceValue(addonsLine as ICakeCartLine);
      const upsellLines = values(lines || {})
        .filter(({ count, id }) => count || (this.isCateringMenu && id))
        .map((line) => ({
          ...line,
          imagesForPrinting: filteredAddonsLine.imagesForPrinting,
          questionsMap: filteredAddonsLine.questionsMap,
          extraProductsMap: filteredAddonsLine.extraProductsMap,
        }));

      acc.push(...upsellLines);
      return acc;
    }, []);
  }

  private _updateTotalPriceAndQuantity() {
    const discount = this.cake.discount && this.cake.discount.value || null;

    if (this.isPiecesGroupType) {
      this._mergeMainCartLineWithMultipleVariantsCartLine();

      const {
        total: multiVariantsTotal,
        quantity: multiVariantsQuantity
      } = getPiecesMultipleVariantsPriceAndQuantity(this.piecesVariantCartLines, discount);

      this.totalPrice = multiVariantsTotal;
      this.totalQuantity = multiVariantsQuantity;
    } else if (this.isConcreteProductType) {
      this.totalPrice = getCakeCartLineTotalPrice({ line: this.cartLine, discount });
      this.totalQuantity = this.cartLine.count;
    }

    if (this.upsellProductTypes?.length) {
      const {
        total: upsellTotal,
        quantity: upsellQuantity
      } = getUpsellGroupedPriceAndQuantity(this.upsellAddonsByProductTypeId);
      this.totalPriceUpsell = upsellTotal;
      this.totalQuantityUpsell = upsellQuantity;
    }
  }

  private _mergeMainCartLineWithMultipleVariantsCartLine() {
    const { imagesForPrinting, questionsMap, extraProductsMap, deposit } = this.cartLine;
    const lines = values(this.piecesVariantCartLines)
      .map(line => ({ ...line, imagesForPrinting, questionsMap, extraProductsMap, deposit }));
    this.piecesVariantCartLines = keyBy(lines, 'productId');
  }

  private _checkPiecesVariantSaving() {
    const minQuantity = this.cake.minQuantity;
    const lines = values(this.piecesVariantCartLines);
    const totalQuantity = lines.reduce((acc, line) => acc + line.count, 0);

    return lines.some((l) => l.id || l.count) && totalQuantity >= minQuantity;
  }

  private _initCartLines(cartLines: ICakeCartLine[]) {
    this.cartLines = cloneDeep(cartLines || []);
    this.piecesVariantCartLines = keyBy(this.cartLines, 'productId');
    this.countPerVariantMap = {};

    if (!this.cartLines.length) {
      this.isPiecesVariantsCanBeSaved = false;
      this._updateTotalPriceAndQuantity();
      return;
    }

    forOwn(this.piecesVariantCartLines, (line, productId) => {
      this.countPerVariantMap = extend(this.countPerVariantMap, { [Number(line.variant.id)]: line.count });
      extendWithPrice(this.piecesVariantCartLines[productId], this.cartLine.size, line.product);
    });

    this.isPiecesVariantsCanBeSaved = this._checkPiecesVariantSaving();
    this._updateTotalPriceAndQuantity();
  }

  private _initUpsellCartLines(upsellCartLines: ICakeCartLine[]) {
    this.upsellAddonsByProductTypeId = getUpsellCartLinesByProductTypeId({
      upsellProductTypes: this.upsellProductTypes || [],
      upsellCartLines,
      parentType: this.cake && this.cake.id
    });
    this.canUpsellsBeSaved = this._canUpsellsBeSaved();

    this._updateTotalPriceAndQuantity();
  }

  private _subscribe() {
    this.addSubscriber(combineLatest(this._subjectsSet['cake'], this._subjectsSet['cartLine'])
      .subscribe(([cake, cartLine]) => {
        if (!cake || !cartLine) {
          return;
        }

        this.cartLine = cloneDeep(cartLine);

        this._init();
      }));
  }

  private _init() {
    if (this.isConcreteProductType) {
      this.cartLine.extraProductsMap = this.cartLine.extraProductsMap || {};
      this.cartLine.questionsMap = this.cartLine.questionsMap || {};
      this.cartLine.deposit = this.cartLine.deposit || {};

      if (this.cake.greetingCard.available) {
        this.cartLine.greetingCard = this.cartLine.greetingCard || {
          text: '',
          checked: false,
          price: this.cake.greetingCard.price
        };
      }

      this._onCakeLoad();
    }

    this._updateTotalPriceAndQuantity();
    this.loaded = true;
  }


  private _onCakeLoad() {
    this.sizes = extendWithMinPrice(this.cake.sizes);

    if (this.cartLine.size) {
      this._getSelectedSizeAndUpdate(this.sizes, this.cartLine.size.details.id);
    } else if (this.cake.pieces || this.cake.sizes.length === 1) {
      this._getSelectedSizeAndUpdate(this.sizes, this.cake.sizes[0].details.id);
    } else {
      this._aggregateCakeDisplayingInfo(this.sizes);
    }
  }

  private _getSelectedSizeAndUpdate(sizes, sizeId) {
    sizeId = get(this.cartLine, 'size.details.id') || Number(sizeId);
    const selectedSize = find(sizes, (s) => s.details.id === sizeId);
    if (selectedSize) {
      this.onSizeUpdated(selectedSize);
    }
  }

  private _reviseExtraProducts(cartLine: ICakeCartLine, newSize) {
    const groups = newSize.extraGroups
      .map((group) => this._getExtraProductGroup(group))
      .filter((group) => group.items.length > 0);

    if (!groups || groups.length === 0) {
      cartLine.extraProductsMap = {};
      this.extraProductGroups = [];
      return;
    }

    // to reduce extension that are not available for new cakeSize/cakeFilling
    const extensions = {};

    groups.forEach((group) => {
      if (this._isExtraGroupSelected(group)) {
        const extraProductsMapIds = map(cartLine.extraProductsMap[group.id], (p) => p.id);
        const selectedProducts = filter(group.items, (i) => {
          return includes(extraProductsMapIds, i.id);
        });

        const productsToApplyLimit = selectedProducts.length > 0
          ? selectedProducts
          : getDefaultSelection(group.items);

        extensions[group.id] = applySelectionLimit(group, productsToApplyLimit,);
      } else if (group.preselectFirstChoice && group.type !== 'YES_NO' && !this._isEditingSavedLine(cartLine)) {
        extensions[group.id] = getPreselectedSelection(group.items);
      } else if (group.maxSelections === 1 && group.type === 'YES_NO') {
        extensions[group.id] = getDefaultSelection(group.items);
      }
      group.addonType = ADDONS_TYPES.EXTRA_PRODUCT;
    });

    cartLine.extraProductsMap = extensions;
    this.extraProductGroups = groups;
  }

  private _reviseQuestions(cartLine: ICakeCartLine, sizeQuestions: any[]) {
    if (!sizeQuestions || sizeQuestions.length === 0) {
      cartLine.questionsMap = {};
      this.questions = [];
      return;
    }

    // fix cart lines management, each sub line has question:id property
    sizeQuestions.forEach((q) => q.question = q.id);
    this.questions = sizeQuestions.map(q => ({ ...q, addonType: ADDONS_TYPES.QUESTION }));

    // to reduce extension that are not available for new cakeSize/cakeFilling
    const fulfilledQuestions = sizeQuestions
      .map((q) => {
        const existingQuestion = cartLine.questionsMap[q.id];
        if (existingQuestion) {
          return {
            ...q,
            answer: existingQuestion.answer
          };
        }

        return q;
      });

    cartLine.questionsMap = keyBy(fulfilledQuestions, 'id');
  }

  private _isExtraGroupSelected(group) {
    return this.cartLine.extraProductsMap[group.id] !== undefined;
  }

  // a line restored from the cart/order omits groups the user left empty, so pre-picking would re-add them
  private _isEditingSavedLine(cartLine: ICakeCartLine): boolean {
    return Boolean(cartLine.id || cartLine.orderLineBuilderId);
  }

  private _reviseVariants(cartLine: ICakeCartLine, cakeSize) {
    const newFillings = this._getSizeFillings(cakeSize);

    if (!this.variants) {
      this.variants = newFillings;
    } else {
      this.variants = dataHelper.leftJoin(this.variants, newFillings, 'id');
    }

    this.variants = extendWithPriceDiff(this.variants, cakeSize);
    this.variants = orderBy(this.variants, ['priority'], ['asc']);

    if (cartLine.variant) {
      cartLine.variant = find(this.variants, { id: cartLine.variant.id });

      if (!cartLine.variant) {
        delete cartLine.variant;
      }
    }
  }

  private _reviseRequiredSections(cartLine: ICakeCartLine, cakeSize) {
    if (this.canHasMotive) {
      if (cakeSize.printingPictures && cakeSize.printingPictures.length) {
        this.requiredSections.printingPictures = cakeSize.printingPictures
          .filter(picture => picture.requiredForCustomer)
          .map(picture => {
            let printingPicture;
            if (cartLine.imagesForPrinting && cartLine.imagesForPrinting.length) {
              printingPicture = cartLine.imagesForPrinting.find(p => p.id === picture.id);
            }
            return {
              id: picture.id,
              checked: Boolean(printingPicture && printingPicture.url)
            };
          });
      } else {
        this.requiredSections.printingPictures = [];
      }
    }

    forOwn(this.requiredSections, (section) => {
      this.isRequirementsExist = this.isRequirementsExist || Boolean(section.length);
    });

    if (this.isRequirementsExist) {
      this._checkRequirementsFilling();
    }
  }

  private _reviseDeposit(cakeSize) {
    if (cakeSize.deposits && cakeSize.deposits.length) {
      this.deposits = cakeSize.deposits;
      this.cartLine.deposit = {
        ...cakeSize.deposits[0],
        deposit: cakeSize.deposits[0].id
      };
    } else {
      this.deposits = [];
      this.cartLine.deposit = {};
    }
  }

  private _checkRequirementsFilling() {
    forOwn(this.requirementsFilling, (requirements, key) => {
      if (key === 'printingPictures') {
        this.requiredSections[key]
          .forEach(({ id, checked }) => this.requirementsFilling[key][id] = checked);
      } else {
        this.requirementsFilling[key] = every(this.requiredSections[key], r => r.checked);
      }
    });

    this.isRequirementsFilled = every(values(this.requirementsFilling), value => {
      if (isObject(value)) {
        return every(values(value), r => r);
      }
      return value;
    });
  }

  private _initRequirements() {
    this.isRequirementsFilled = true;
    this.isRequirementsExist = false;
    this.requiredSections = {
      printingPictures: []
    };
    this.requirementsFilling = {
      printingPictures: {},
    };
  }

  private _onSizeUpdated(size: any) {
    this._initRequirements();

    this._reviseVariants(this.cartLine, size);
    this._reviseQuestions(this.cartLine, size.questions);
    this._reviseExtraProducts(this.cartLine, size);
    this._reviseRequiredSections(this.cartLine, size);
    this._reviseDeposit(size);

    if (this.variants && this.variants.length === 1) {
      const [variant] = this.variants;
      this.cartLine.variant = variant;
    }

    if (!this.canHasMotive()) {
      this.cartLine.imagesForPrinting = [];
      this.printingPictures = [];
      this.printingPictureMap = {};
    } else {
      this.printingPictures = get(this.cartLine.size, 'printingPictures', [])
        .map((pic) => ({ ...pic, addonType: ADDONS_TYPES.PRINTING_PICTURE }));

      this.printingPictureMap = this.makePictureMap(this.printingPictures);
    }

    this._updateProductInfo();

    this.sortingAddons = this._getSortingAddons({
      extraProductGroups: this.extraProductGroups,
      questions: this.questions,
      printingPictures: this.printingPictures,
      deposits: this.deposits
    });
  }

  private makePictureMap(printingPictures) {
    const picById = (this.cartLine.imagesForPrinting || [])
      .reduce((acc, printing) => (acc[printing.id] = printing, acc), {});

    return printingPictures
      .reduce((acc, picture) => {
        if (picById[picture.id]) {
          acc[picture.id] = picById[picture.id];
        }
        return acc;
      }, {});
  }

  private _getSortingAddons(params: { extraProductGroups: any[], questions: any[], printingPictures: any[], deposits: any[] }) {
    const { extraProductGroups, questions, printingPictures, deposits } = params;

    const requiredCustomerPictures = [];
    const notRequiredCustomerPictures = [];
    printingPictures.forEach(picture => picture.requiredForCustomer
      ? requiredCustomerPictures.push(picture)
      : notRequiredCustomerPictures.push(picture));

    const addOns = [
      ...extraProductGroups,
      ...questions,
      ...notRequiredCustomerPictures,
      ...deposits
    ]
      .filter(a => Boolean(a))
      .sort((a, b) => a.priority - b.priority);
    return [...requiredCustomerPictures, ...addOns];
  }

  private _aggregateCakeDisplayingInfo(sizes) {
    const sizesMaxVariants: any = maxBy(sizes, 'products.length');
    const sizesMaxQuestions: any = maxBy(sizes, 'questions.length');
    const sizesMaxExtraGroups: any = maxBy(sizes, 'extraGroups.length');
    const sizesMaxPrintingPictures: any = maxBy(sizes, 'printingPictures.length');
    const sizesMaxDeposit: any = maxBy(sizes, 'deposits.length');

    this.printingPictures = sizesMaxPrintingPictures.printingPictures;
    if (this.printingPictures && this.printingPictures.length) {
      this.printingPictures
        .forEach(picture => picture.addonType = ADDONS_TYPES.PRINTING_PICTURE);
    }

    this._reviseVariants(this.cartLine, sizesMaxVariants);
    this._reviseQuestions(this.cartLine, sizesMaxQuestions.questions);
    this._reviseExtraProducts(this.cartLine, sizesMaxExtraGroups);
    this._reviseDeposit(sizesMaxDeposit);

    this.sortingAddons = this._getSortingAddons({
      extraProductGroups: this.extraProductGroups,
      questions: this.questions,
      printingPictures: this.printingPictures,
      deposits: this.deposits,
    });
  }

  private _getSizeFillings(size) {
    const products = size.products;
    return map(products, (product) => extend({}, product.variant, { price: product.price || size.price, image: product.image }));
  }

  private _getExtraProductGroup(group: any): any {
    return getExtraProductGroup(group, this._translate.instant('cakeBuilder.extraProductsSection.noChoice'));
  }

  private _updateProductInfo() {
    const { size, variant } = this.cartLine;

    if (!size || !variant || !this.cake) {
      return;
    }

    const product = find(size.products, (product) => {
      return product.variant.id === variant.id;
    });

    extendWithPrice(this.cartLine, size, product);
    this.cartLine.productId = product.id;
    this.cartLine.product = makeSizeProductForOrderLine(this.cake, product);
  }

  private _scrollToSection(sectionRef: ElementRef<HTMLElement>, duration = 450, extraOffset = 0) {
    if (this.disabledScrollingToSection) {
      return;
    }

    setTimeout(() => {
      if (!sectionRef) {
        return;
      }

      let offset = -100;

      if (this.uiService.isMd()) {
        offset = -200;
      }

      if (this.uiService.isLg() || this.uiService.isXl()) {
        offset = -300;
      }

      const config: ScrollToConfigOptions = {
        target: sectionRef,
        offset: offset + extraOffset,
        duration
      };

      this._scrollTo.scrollTo(config);
    });
  }

  private _addOnsIsEmpty() {
    return isEmpty(this.extraProductGroups) && isEmpty(this.questions) && isEmpty(this.deposits)
      && isEmpty(this.printingPictures) && !this.cake.greetingCard.available;
  }

  private _trackSubmit(event: ISubmitBuilderPayload) {
    const { goNext, addMore } = event;
    getActualLinesFromEvent(event).forEach(cartLine => {
      errorHandler(() => {
        const toTrack = {
          price: getCakeCartLineTotalPrice({ line: cartLine }),
          count: cartLine.count,
          hasGreetingCard: Boolean((cartLine.greetingCard && cartLine.greetingCard.text && cartLine.greetingCard.checked)),
          hasMotive: cartLine.imagesForPrinting && cartLine.imagesForPrinting.length,
          extraProductsCount: reduce(cartLine.extraProductsMap, (current, group) => current + group.length, 0),
          goNext,
          addMore
        };
      });
    });
  }

  private _getDetails(): IQuestionHelpTextDetails {
    const defaultDetails = {
      sizeSectionTitle: this._translate.instant('defaultQuestionAndHelpText.sizeSectionTitle'),
      filingSectionTitle: this._translate.instant('defaultQuestionAndHelpText.filingSectionTitle'),
      sizeSectionHelpText: this._translate.instant('defaultQuestionAndHelpText.sizeSectionHelpText'),
    };

    // An empty help text means the seller cleared it, so only a missing one falls back to the next source.
    // An empty title still falls back.
    return mergeWith(defaultDetails, this.bakeryDetails, this.cake.details, (objVal, srcVal, key) => {
      if (HELP_TEXT_KEYS.includes(key)) {
        return isNil(srcVal) ? objVal : srcVal;
      }

      return srcVal || objVal;
    });
  }

}
